import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { ArrowLeft, Plus } from "@/lib/icons";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/app/shell/page-header";
import { AriaScheduleDialog } from "@/components/app/assistant/schedules/aria-schedule-dialog";
import { AriaScheduleList } from "@/components/app/assistant/schedules/aria-schedule-list";
import { AriaNavigate } from "@/components/app/assistant/blocks/navigate";
import type { AriaScheduleView } from "@/components/app/assistant/schedules/schedule-types";
import { orpc } from "@/lib/orpc";
import { guardFeature } from "@/lib/policy/route-guard";

export const Route = createFileRoute("/_authenticated/aria/schedules")({
  beforeLoad: ({ context }) => guardFeature(context.me, "aria-schedules"),
  component: AriaSchedulesPage,
});

/* A page of its own in Aria's workspace rather than a drawer over the thread: a
   schedule has a form, a history and a delete, and none of that belongs a stray
   click from the conversation. */
function AriaSchedulesPage() {
  const navigate = Route.useNavigate();
  /* A row in a run's table links into the app, as it does from the thread. */
  const follow = useCallback((href: string) => void navigate({ to: href }), [navigate]);
  const status = useQuery(orpc.support.assistant.status.queryOptions());
  const running = status.data?.schedules === true;
  const [dialog, setDialog] = useState<{ open: boolean; editing: AriaScheduleView | null }>({
    open: false,
    editing: null,
  });

  return (
    <AriaNavigate.Provider value={follow}>
      {/* Bottom room for the phone dock, which floats over the page's last card. */}
      <div className="w-full px-4 pt-6 pb-[calc(1.5rem+var(--dock-h,0px))] md:px-6 xl:px-8">
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="text-muted-foreground -ml-2 mb-3 gap-1.5"
        >
          <Link to="/aria" search={{}}>
            <ArrowLeft className="size-4" />
            Back to Aria
          </Link>
        </Button>
        <PageHeader
          title="Schedules"
          meta="Tasks Aria runs on her own at the times you choose. Each run goes to its own conversation, and you are told when it ran."
          actions={
            <Button
              onClick={() => setDialog({ open: true, editing: null })}
              disabled={status.isSuccess && !running}
            >
              <Plus className="size-4" />
              Create schedule
            </Button>
          }
        />
        {status.isSuccess && !running ? (
          <Alert className="mt-4">
            <AlertTitle>Schedules are switched off</AlertTitle>
            <AlertDescription>
              Aria cannot run one until they are on. Ask People AI support to switch them on.
            </AlertDescription>
          </Alert>
        ) : null}
        <div className="mt-6">
          <AriaScheduleList
            onEdit={(schedule) => setDialog({ open: true, editing: schedule })}
            switchedOff={status.isSuccess && !running}
            emptyAction={
              running ? (
                <Button variant="outline" onClick={() => setDialog({ open: true, editing: null })}>
                  <Plus className="size-4" />
                  Create schedule
                </Button>
              ) : undefined
            }
          />
        </div>
        <AriaScheduleDialog
          open={dialog.open}
          editing={dialog.editing}
          onOpenChange={(open) => setDialog((was) => ({ ...was, open }))}
        />
      </div>
    </AriaNavigate.Provider>
  );
}
